// keep Pinia at the top of the imports
import { pinia } from '@/pinia';
import { createApp } from 'vue';
import App from './App.vue';
import { Capacitor } from '@capacitor/core';
import router from './router';
import { IonicVue } from '@ionic/vue';
import { VueQueryPlugin, type VueQueryPluginOptions } from '@tanstack/vue-query';
import { persistQueryClient as persistQueryClientCore } from '@tanstack/query-persist-client-core';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { initializeApp } from 'firebase/app';
import firebaseConfig from '@/firebase';
import i18n from '@/i18n';
import { bootstrapSentry } from '@/sentry';
import VueTheMask from 'vue-the-mask';
import VueRecaptcha, { VueReCaptcha } from 'vue-recaptcha-v3';
import { CapacitorReadNativeSetting } from 'capacitor-read-native-setting';
import { GesturePlugin } from '@vueuse/gesture';
import VueDOMPurifyHTML from 'vue-dompurify-html';
import { defineCustomElements } from '@ionic/pwa-elements/loader';
import useStorageService from './shared/composables/useStorageService';
import useTenant from './shared/composables/useTenant';
import { useCacheImage } from '@/shared/composables/useCacheImage';
import 'flag-icons/css/flag-icons.min.css';

/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/float-elements.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/text-transformation.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/display.css';

/* Theme variables */
import './theme/variables.css';
import './theme/global.scss';
import NativeAppConfig from '@/config/native-app.config.json';

console.log('============ APP (RE)STARTING ===============');
bootstrapSentry();

const { get, set } = useStorageService();
const { getConfig, getStyles } = useTenant();

// TanStack persist configuration
const vueQueryOptions: VueQueryPluginOptions = {
	queryClientConfig: {
		defaultOptions: {
			queries: {
				cacheTime: 1000 * 60 * 60 * 24 * 365,
				retry(failureCount: number, error: any) {
					// Retry logic
					// If error is 403 don't retry again
					// Else, retry up to 3 times
					// This can be overwritten by local configuration on each call
					const retryTimes = 3;
					return error && error.response && error.response.status === 403 ? false : failureCount < retryTimes - 1 ? true : false;
				},
			},
		},
	},
	clientPersister: (queryClient: any) => {
		return persistQueryClientCore({
			queryClient,
			persister: createSyncStoragePersister({ storage: localStorage }),
		});
	},
};

// Initialize Firebase
initializeApp(firebaseConfig);

// Register service worker
if ('serviceWorker' in navigator) {
	navigator.serviceWorker
		.register('firebase-messaging-sw.js')
		.then((reg) => {
			console.log(`Service Worker Registration (Scope: ${reg.scope})`);
		})
		.catch((error) => {
			const msg = `Service Worker Error (${error})`;
			console.error(msg);
		});
} else {
	// happens when the app isn't served over HTTPS or if the browser doesn't support service workers
	console.warn('Service Worker not available');
}

// Internationalization (see src/i18n.ts)

const RECAPTCHAS_SITE_KEY = process.env.VUE_APP_RECAPTCHAS_SITE_KEY;

// Call the element loader after the app has been rendered the first time
defineCustomElements(window);

const app = createApp(App)
	.use(IonicVue)
	.use(pinia)
	.use(VueQueryPlugin, vueQueryOptions)
	.use(i18n)
	.use(GesturePlugin)
	.use(VueDOMPurifyHTML, {
		default: {
			ADD_TAGS: ['iframe', 'p', 'br', 'strong'],
			ADD_ATTR: ['url'],
		},
	})
	.use(VueTheMask)
	.use(VueReCaptcha, {
		siteKey: RECAPTCHAS_SITE_KEY as string,
		loaderOptions: {}, // Add appropriate loader options here
	});

const saveInCache = async (key: string, fn: any) => {
	let keys = await get('cachedDataKeys');
	if (!keys) {
		keys = [key];
	} else {
		if (!keys.includes(key)) {
			keys.push(key);
		}
	}
	await set('cachedDataKeys', keys);

	let data = await get(key);
	if (!data) {
		try {
			// Get the data from the server
			data = await fn();
			// If data is empty (no connection will fail, then returning empty data), don't save it
			// on the Preferences
			// Also, check if the data looks correct
			if (data && Object.keys(data).length > 0 && ('name' in data || 'enable_public_mode' in data)) {
				await set(key, data);
			}
		} catch (error) {
			console.error('Error getting data from server', key, error);
		}
	}
};

const setStartMessage = () => {
	const startMessages = (i18n.global.messages.value as any)[i18n.global.locale.value].start_messages;
	const startMessage = startMessages[Math.floor(Math.random() * startMessages.length)];
	document.getElementById('loadingMessage')!.innerHTML = `${startMessage}...`;
};

const setInternetMessageAndShowRetryButton = () => {
	const locale = i18n.global.locale.value;
	const msgs = (i18n.global.messages.value as any)[locale];
	const internetMessage = msgs.general.connection.loading_app;
	const retryText = msgs.general.connection.loading_app_retry;
	document.getElementById('loadingMessage')!.innerHTML = internetMessage;
	document.getElementById('retry-button')!.innerHTML = retryText;
	document.getElementById('retry-button')!.style.display = 'block';
};

const setErrorMessages = (error: any) => {
	const errorMessages = JSON.stringify(error);
	const retryText = (i18n.global.messages.value as any)[i18n.global.locale.value].general.connection.loading_app_retry;
	document.getElementById('loadingMessage')!.innerHTML = errorMessages;
	document.getElementById('retry-button')!.innerHTML = retryText;
	document.getElementById('retry-button')!.style.display = 'block';
};

const patchIndexHtml = async () => {
	const { loadImage } = useCacheImage();

	// Get the app background and logo elements from index.html
	const appBackgroundElement = document.getElementById('app-background');
	const appLogoElement = document.getElementById('app-logo');

	if (Capacitor.isNativePlatform()) {
		// Get the logo and background image from the native settings
		let logoUrl = NativeAppConfig.LOGO_URL;
		let backgroundUrl = NativeAppConfig.BACKGROUND_URL;

		if (Capacitor.isPluginAvailable('CapacitorReadNativeSetting')) {
			const logoUrlFromNative = await CapacitorReadNativeSetting.read({ key: 'logo_url' });
			const backgroundUrlFromNative = await CapacitorReadNativeSetting.read({ key: 'bg_image_url' });
			console.log('logoUrlFromNative', logoUrlFromNative);
			console.log('backgroundUrlFromNative', backgroundUrlFromNative);

			let logoImage = logoUrlFromNative.value.split('/').pop();
			logoImage = logoImage ?? 'logo';
			logoUrl = await loadImage('logo', logoImage, logoUrlFromNative.value);

			let backgroundImage = backgroundUrlFromNative.value.split('/').pop();
			backgroundImage = backgroundImage ?? 'bg';
			backgroundUrl = await loadImage('background', backgroundImage, backgroundUrlFromNative.value);

			console.log('logoUrl from cache', logoUrl);
			console.log('backgroundUrl from cache', backgroundUrl);
		}

		// Patch the background if it exists
		if (appBackgroundElement) {
			appBackgroundElement.style.background = `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('${backgroundUrl}') no-repeat center center / cover`;
		}

		// Patch the logo if it exists
		if (appLogoElement) {
			appLogoElement!.style.display = 'block';
			(appLogoElement as HTMLImageElement).src = logoUrl;
		}
	} else {
		// Patch the logo if it exists
		if (appLogoElement) {
			appLogoElement!.style.display = 'block';
		}
	}
};

const initApp = async () => {
	try {
		// Patch the index.html with the native icon and background if they doesn't exist
		patchIndexHtml();

		// Set the start message
		setStartMessage();

		// Save the tenant config and styles in cache
		await saveInCache('tenantConfig', getConfig);
		await saveInCache('tenantStyles', getStyles);

		// Check if tenantConfig and tenantStyles are loaded
		const tenantConfig = await get('tenantConfig');
		const tenantStyles = await get('tenantStyles');

		console.log('tenantConfig', tenantConfig);
		console.log('tenantStyles', tenantStyles);

		if ((window.apiUrl === 'REPLACE_API_URL' && !tenantConfig) || (tenantConfig && Object.keys(tenantConfig).length === 0) || !tenantStyles || (tenantStyles && Object.keys(tenantStyles).length === 0)) {
			setInternetMessageAndShowRetryButton();
			return;
		}
	} catch (error) {
		setErrorMessages(error);
		return;
	}

	// Initialize router after cache is initialized
	app.use(router);

	// Wait for router to be ready
	router.isReady().then(() => {
		app.mount('#app');
	});
};

initApp();
